/**
 * The map's layout, pure and unit-testable: a lane per system, the data models in columns in the order
 * their records come (the time-ordered greedy columns of components/data/Journey.tsx, made a function),
 * and the integrations routed as orthogonal paths with rounded corners (after prototype C). A flow leaves
 * its source from the right, runs in the gap between two columns (parallel flows on their own offset) and
 * enters its target from the left; when it must cross columns or lanes, or go back, it travels along the
 * boundary between two lanes, where nothing sits (prototype B's bus). A flow that sends out of the map ends
 * at a dashed ghost in the same lane that names where it went and what happened there. Lanes are ordered so
 * that systems joined by flows are neighbours. Each column is as wide as its widest node's one-line title
 * and sub-line ask; when the width is short the gaps tighten first, then the sub-lines wrap so the titles
 * alone size the columns, and only then the map grows past its width and scrolls, so no node is ever hidden
 * or cut. A label sits beside the longest straight run of its own path, at the arriving end, clear of the
 * documents; stacked labels sit a row apart with a tick to their arrow. Nothing here touches the DOM: text
 * is measured by the `measure` option when the renderer can, by an estimate otherwise.
 */
import dayjs from 'dayjs';
import { count } from '../lib/format.ts';
import type { Lane, LayoutOptions, MapFlow, MapLayout, MapLink, MapNode, PlacedNode, RoutedFlow } from './types.ts';

/** The lane label column on the left (badge + system name). */
export const LABEL_W = 128;
const PAD_R = 8;
/** A node's width when the map is roomy; narrower when the width is short, never under the minimum. */
const NODE_W = 168;
const NODE_W_MIN = 120;
/** The floor once the sub-lines wrap and the titles alone size the columns (a title is never cut). */
const NODE_W_TIGHT = 108;
/** A title or sub-line that would ask for more than this wraps instead: a node does not grow without end. */
const NODE_W_MAX = 240;
/** Between two columns of nodes: room for the vertical runs of flows and their offsets. */
const GAP_MIN = 32;
/** Columns move this close when the width is short, before the map grows and scrolls. */
const GAP_TIGHT = 24;
/** Past this gap a sparse map widens its nodes instead of its gaps. */
const GAP_WIDE = 96;
/** Lanes adapt between these; when even the minimum does not fit `maxHeight`, the height wins (see `fit`). */
const LANE_MIN = 52;
const LANE_MAX = 96;
const NODE_H = 56;
const NODE_H_MIN = 42;
const NODE_PAD_Y = 5;
const NODE_PAD_X = 12;
const LINE_H: Record<number, number> = { 15: 18, 13: 16 };
const DETAIL_H = 14;
const CORNER = 10;
/** The dashed ghost at the end of an out-flow: its padding and the most it is wide before its name wraps. */
const GHOST_PAD_X = 12;
const GHOST_W_MAX = 160;
const GHOST_W_MIN = 72;
/** Stacked ghosts (two destinations from one node) sit this far apart. */
const GHOST_STACK = 4;
/** A bus below the last lane, when a flow needs one. */
const BUS_H = 28;
/** Arrows sharing a node edge leave at distinct heights, this far apart at most (top · middle · bottom). */
const END_STEP = 12;
const END_PAD = 9;
const LABEL_SIZE = 11;
/** A label is a pill this tall; the pills of a bundle on one corridor stack this far apart. */
const LABEL_H = 14;
const LABEL_STACK = 14;
/** A split fans into at most this many documents, a merge folds as many: more is clutter, not information. */
export const MAX_FAN = 3;
const MASTER = -1e12;
/** What a failing flow adds to its weight when lanes are ordered: as much as a flow of this many runs. */
const FAIL_WEIGHT = 300;

export interface Font { size: number; weight: number; mono: boolean }
export type Measure = (text: string, font: Font) => number;
/** Without a canvas: Geist runs about half an em per glyph, Geist Mono six tenths. */
export const estimate: Measure = (text, f) => text.length * f.size * (f.mono ? 0.6 : f.weight >= 600 ? 0.56 : 0.52);

const TITLE: Font = { size: 15, weight: 600, mono: false };
const TITLE_S: Font = { size: 13, weight: 600, mono: false };
const GHOST_F: Font = { size: 13, weight: 500, mono: false };
const MONO: Font = { size: 11, weight: 500, mono: true };

type Pt = { x: number; y: number };
export type Primitive =
  | { kind: 'L'; x0: number; y0: number; x1: number; y1: number; len: number }
  | { kind: 'A'; cx: number; cy: number; r: number; a0: number; sweep: 1 | -1; len: number };
/** An orthogonal path: its corner points, the lines and arcs it is drawn with, and the SVG data. */
export interface Route { pts: Pt[]; prims: Primitive[]; d: string; length: number }

export interface PlacedNodeX extends PlacedNode {
  cx: number;
  cy: number;
  /** The label as it is set in the node: one line at 15 px when it fits, else 13 px, else wrapped. */
  labelLines: string[];
  labelSize: number;
  /** "✕ 11 failed", on the detail line when both fit, else on a line of its own (the node grows). */
  failText: string;
  failOwnLine: boolean;
  /** The red line as set: one line, or broken at its spaces over two when the columns are squeezed. */
  failLines: string[];
  /** The detail line, broken at its separator into two when it is wider than the node: never cut. */
  detailLines: string[];
}
/**
 * Where an out-of-the-map flow ends: a dashed pill in the source's lane naming the external target
 * ("e-invoice operator", "customers") and what happened there ("never arrived", "none since 17:37"). Not a
 * node: nothing to open, it lights up with its flow. Two flows to the same place from one node share one.
 */
export interface Ghost {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  lane: number;
  labelLines: string[];
  fact: string | null;
  failed: boolean;
  flowIds: string[];
}
export interface Fan { d: string; length: number; swapAt: number; route: Route }
export interface RoutedFlowX extends RoutedFlow {
  route: Route;
  kind: 'adjacent' | 'corridor' | 'vertical' | 'out';
  /** What the label says (timesLabel when there is one) and how it is anchored at labelAt. */
  labelText: string;
  labelAnchor: 'start' | 'end';
  labelW: number;
  /** The offset paths the glyphs of a split leave on (or of a merge arrive on). */
  fans: Fan[];
  /**
   * The column gap this flow's documents run through (negative: the column of a vertical hop), and the
   * stretch of the path (0..1) spent in it: flows sharing a gap stagger their documents against each other.
   */
  gapAt: number;
  gapWindow: [number, number];
  /** The ghost an out-flow ends at. */
  ghost: Ghost | null;
  /**
   * A bundle: when BUNDLE_MIN or more flows enter one node they share their last stretch (a trunk) and one pill. The
   * flow that carries the bundle's pill says how many it bundles; the others draw no pill of their own.
   */
  bundle?: { n: number; runs: number };
  labelHidden?: boolean;
}

/** From how many flows into one node they share a trunk and a pill. */
export const BUNDLE_MIN = 3;
/** How far a line along a lane's boundary keeps from the nodes (px). */
const NODE_CLEAR = 10;
export interface MapLayoutX extends MapLayout {
  nodes: PlacedNodeX[];
  flows: RoutedFlowX[];
  ghosts: Ghost[];
  /** The typical column (node + gap), the node width columns start from, and the gap between columns. */
  colW: number;
  nodeW: number;
  gap: number;
  /** The size of the glyphs that fits the lanes (1 on tall lanes, smaller on tight ones). */
  glyphBase: number;
  /** The scale the renderer draws the glyphs at: 1.5 × glyphBase × the page's glyphScale. */
  glyphS: number;
}

const round = (v: number) => Math.round(v * 10) / 10;
/** a before b, comparing the first entries that differ. */
const lexLess = (a: number[], b: number[]) => {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] < b[i];
  return false;
};

// ---------------------------------------------------------------- order: offsets, lanes, columns

/**
 * When each data model's records usually come, relative to the anchor: from the hints (a record list's
 * typicalMs), else along the flows (a target after its sources). Without any hint, the sources of the
 * flows start at 0, except a source that lives in a system fed by another flow: it follows what fed its
 * system (deliveries come after the orders the warehouse received), and its own flow goes back.
 */
function offsetsOf(nodes: MapNode[], flows: MapFlow[], hints?: Map<string, number>): Map<string, number> {
  const offset = new Map<string, number>();
  const ids = new Set(nodes.map((n) => n.id));
  for (const [id, v] of hints ?? []) if (ids.has(id)) offset.set(id, v);
  const inner = flows.filter((f) => f.to && f.to !== f.from && ids.has(f.from) && ids.has(f.to));
  const incoming = (c: string) => inner.filter((f) => f.to === c).map((f) => f.from);
  const outgoing = (c: string) => inner.filter((f) => f.from === c).map((f) => f.to!);
  const deferred = new Set<string>();
  if (offset.size === 0) {
    const systemOf = (id: string) => nodes.find((n) => n.id === id)!.system;
    // A source whose system is fed by the very system it sends to answers that system: it comes after
    for (const n of nodes) {
      if (incoming(n.id).length > 0) continue;
      const answers = outgoing(n.id).some((t) => inner.some((f) => systemOf(f.to!) === n.system && systemOf(f.from) === systemOf(t)));
      if (answers) deferred.add(n.id);
      else offset.set(n.id, 0);
    }
  }
  const backward = (f: MapFlow) => deferred.has(f.from);
  const all = nodes.map((n) => n.id);
  let unknown = all.filter((c) => !offset.has(c) && !deferred.has(c));
  for (let i = 0; i < 20 && unknown.length; i++) {
    for (const c of unknown) {
      const from = inner.filter((f) => f.to === c && !backward(f) && offset.has(f.from)).map((f) => offset.get(f.from)!);
      const to = inner.filter((f) => f.from === c && !backward(f) && offset.has(f.to!)).map((f) => offset.get(f.to!)!);
      if (from.length) offset.set(c, Math.max(...from) + 1);
      else if (to.length) offset.set(c, Math.min(...to) - 1);
    }
    unknown = all.filter((c) => !offset.has(c) && !deferred.has(c));
  }
  for (const c of deferred) {
    const system = nodes.find((n) => n.id === c)!.system;
    const siblings = nodes.filter((n) => n.system === system && offset.has(n.id)).map((n) => offset.get(n.id)!);
    offset.set(c, siblings.length ? Math.max(...siblings) + 1 : 0);
  }
  // Master data without links: first
  all.filter((c) => !offset.has(c)).forEach((c, i) => offset.set(c, MASTER + i));
  return offset;
}

/**
 * Lane order by adjacency: systems joined by a flow sit next to each other, so an arrow between them is a
 * short hop rather than a vertical across the map. The hint's lanes come first, in the hint's order; every
 * other system joins one by one, the one most connected to what is placed first (the busiest when none is),
 * in the slot where the arrows stay shortest in all: the sum, over every pair of systems joined by flows,
 * of their runs times the lanes between them (a failing flow counts as a busy one, so the red hop is the
 * one kept short). Ties fall to the time order (upstream above); systems joined to nothing go to the bottom.
 */
function laneOrderOf(nodes: MapNode[], flows: MapFlow[], links: MapLink[], offset: Map<string, number>, hint?: string[]): string[] {
  const systems = [...new Set(nodes.map((n) => n.system))];
  const sysOf = (id: string) => nodes.find((n) => n.id === id)?.system;
  const key = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const weight = new Map<string, number>();
  const join = (a: string | undefined, b: string | undefined, w: number) => {
    if (a && b && a !== b) weight.set(key(a, b), (weight.get(key(a, b)) ?? 0) + w);
  };
  for (const f of flows) join(sysOf(f.from), f.to ? sysOf(f.to) : undefined, Math.max(1, f.runs) + (f.failed > 0 ? FAIL_WEIGHT : 0));
  for (const l of links) join(sysOf(l.from), sysOf(l.to), 1);
  const w = (a: string, b: string) => weight.get(key(a, b)) ?? 0;
  const docs = (s: string) => nodes.filter((n) => n.system === s).reduce((a, n) => a + (n.count ?? 0), 0);
  // The system's earliest timed node; master data alone counts as earliest of all
  const minOffset = (s: string) => {
    const timed = nodes.filter((n) => n.system === s).map((n) => offset.get(n.id)!).filter((v) => v > MASTER / 10);
    return timed.length ? Math.min(...timed) : MASTER;
  };
  const order = (hint ?? []).filter((s, i, all) => systems.includes(s) && all.indexOf(s) === i);
  const remaining = systems.filter((s) => !order.includes(s));
  while (remaining.length) {
    const linkTo = (s: string) => order.reduce((acc, t) => acc + w(s, t), 0);
    remaining.sort((a, b) => linkTo(b) - linkTo(a) || docs(b) - docs(a) || a.localeCompare(b));
    const s = remaining.shift()!;
    const joined = linkTo(s) > 0;
    let best: { i: number; cost: number[] } | null = null;
    for (let i = 0; i <= order.length; i++) {
      // The arrows' lengths with s between order[i − 1] and order[i]: its own, and every pair it comes between
      let length = 0;
      order.forEach((t, j) => { length += w(s, t) * (j < i ? i - j : j - i + 1); });
      for (let a = 0; a < i; a++) for (let b = i; b < order.length; b++) length += w(order[a], order[b]);
      const above = order[i - 1];
      const below = order[i];
      const time = joined ? (above && minOffset(s) < minOffset(above) ? 1 : 0) + (below && minOffset(s) > minOffset(below) ? 1 : 0) : 0;
      const cost = [length, time, -i];
      if (!best || lexLess(cost, best.cost)) best = { i, cost };
    }
    order.splice(best!.i, 0, s);
  }
  return order;
}

// ---------------------------------------------------------------- paths

/** Corner points → lines and quarter-circle arcs; a corner's radius never exceeds half of either leg. */
function routeOf(pts: Pt[], radius = CORNER): Route {
  const prims: Primitive[] = [];
  let d = `M${round(pts[0].x)},${round(pts[0].y)}`;
  let cur = pts[0];
  let length = 0;
  const line = (to: Pt) => {
    const len = Math.hypot(to.x - cur.x, to.y - cur.y);
    if (len < 0.01) return;
    prims.push({ kind: 'L', x0: cur.x, y0: cur.y, x1: to.x, y1: to.y, len });
    d += ` L${round(to.x)},${round(to.y)}`;
    length += len;
    cur = to;
  };
  for (let i = 1; i < pts.length - 1; i++) {
    const P = pts[i];
    const A = pts[i - 1];
    const B = pts[i + 1];
    const lA = Math.hypot(P.x - A.x, P.y - A.y);
    const lB = Math.hypot(B.x - P.x, B.y - P.y);
    const r = Math.min(radius, lA / 2, lB / 2);
    if (r < 0.5) {
      line(P);
      continue;
    }
    const din = { x: (P.x - A.x) / lA, y: (P.y - A.y) / lA };
    const dout = { x: (B.x - P.x) / lB, y: (B.y - P.y) / lB };
    const cross = din.x * dout.y - din.y * dout.x;
    if (Math.abs(cross) < 0.01) {
      line(P);
      continue;
    }
    const start = { x: P.x - din.x * r, y: P.y - din.y * r };
    const end = { x: P.x + dout.x * r, y: P.y + dout.y * r };
    line(start);
    const cx = start.x + dout.x * r;
    const cy = start.y + dout.y * r;
    const sweep: 1 | -1 = cross > 0 ? 1 : -1;
    const len = (Math.PI * r) / 2;
    prims.push({ kind: 'A', cx, cy, r, a0: Math.atan2(start.y - cy, start.x - cx), sweep, len });
    d += ` A${round(r)},${round(r)} 0 0 ${sweep > 0 ? 1 : 0} ${round(end.x)},${round(end.y)}`;
    length += len;
    cur = end;
  }
  line(pts[pts.length - 1]);
  return { pts, prims, d, length };
}

/** The point and heading (radians, screen coordinates) at t (0..1) of a route, without the DOM. */
export function pointAlong(route: Route, t: number): { x: number; y: number; angle: number } {
  let left = Math.max(0, Math.min(1, t)) * route.length;
  for (const p of route.prims) {
    if (left > p.len && p !== route.prims[route.prims.length - 1]) {
      left -= p.len;
      continue;
    }
    const u = p.len ? Math.min(1, left / p.len) : 1;
    if (p.kind === 'L') return { x: p.x0 + (p.x1 - p.x0) * u, y: p.y0 + (p.y1 - p.y0) * u, angle: Math.atan2(p.y1 - p.y0, p.x1 - p.x0) };
    const a = p.a0 + p.sweep * (Math.PI / 2) * u;
    return { x: p.cx + p.r * Math.cos(a), y: p.cy + p.r * Math.sin(a), angle: a + (p.sweep * Math.PI) / 2 };
  }
  const last = route.pts[route.pts.length - 1];
  return { x: last.x, y: last.y, angle: 0 };
}

/** Where a route crosses y (or x when `axis` is 'x'), as a share of its length; 0.5 when it does not. */
function crossingAt(route: Route, axis: 'x' | 'y', v: number): number {
  let acc = 0;
  for (const p of route.prims) {
    if (p.kind === 'L') {
      const [a, b] = axis === 'y' ? [p.y0, p.y1] : [p.x0, p.x1];
      if (Math.abs(b - a) > 0.01 && (v - a) * (v - b) <= 0) return (acc + (Math.abs(v - a) / Math.abs(b - a)) * p.len) / route.length;
    }
    acc += p.len;
  }
  return 0.5;
}

/** The k-th of a cycle of offsets 0, −s, +s, −2s, +2s … no further than `max` from the middle. */
function cycled(k: number, step: number, max: number): number {
  const n = 1 + 2 * Math.floor(max / step);
  const i = k % n;
  return i === 0 ? 0 : (i % 2 ? -1 : 1) * Math.ceil(i / 2) * step;
}

type Rect = { x: number; y: number; w: number; h: number };
const hits = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** The text on one line when it fits `avail`, else wrapped at its spaces: never cut. */
function wrapWords(text: string, avail: number, font: Font, measure: Measure): string[] {
  if (measure(text, font) <= avail) return [text];
  const lines: string[] = [];
  let line = '';
  for (const w of text.split(' ')) {
    // A word wider than the line (a technical name: BangBonsomerExecutionRecordsByTimestamp) breaks where its words meet
    const parts = measure(w, font) > avail ? splitLong(w, avail, font, measure) : [w];
    parts.forEach((part, i) => {
      const glue = i === 0 && line ? ' ' : '';
      const next = `${line}${glue}${part}`;
      if (line && measure(next, font) > avail) {
        lines.push(line);
        line = part;
      } else line = next;
    });
  }
  lines.push(line);
  return lines;
}

/** A long word in pieces that each fit: at its camelCase, digit and _ - . boundaries, else character by character. */
function splitLong(word: string, avail: number, font: Font, measure: Measure): string[] {
  const pieces = word.split(/(?<=[a-zäöå0-9])(?=[A-ZÄÖÅ])|(?<=[A-ZÄÖÅ])(?=[A-ZÄÖÅ][a-zäöå])|(?<=[_.\-])/);
  const out: string[] = [];
  for (const piece of pieces) {
    if (measure(piece, font) <= avail) { out.push(piece); continue; }
    let cur = '';
    for (const ch of piece) {
      if (cur && measure(cur + ch, font) > avail) { out.push(cur); cur = ch; } else cur += ch;
    }
    if (cur) out.push(cur);
  }
  return out;
}

/**
 * What the ghost at the end of an out-flow says about its destination. The fact repeats the arrow's own
 * number and kind ("6 runs never arrived" under "⚡ 80 runs · 6 failed"), or the streak ("none since 17:37").
 */
function ghostTextOf(f: MapFlow): { label: string; fact: string | null } {
  const label = f.external?.trim() || 'elsewhere';
  let fact: string | null = null;
  if (f.failing?.since) {
    const since = dayjs(f.failing.since);
    fact = `none since ${since.format(since.isSame(dayjs(), 'day') ? 'HH:mm' : 'ddd HH:mm')}`;
  } else if (f.failed > 0) fact = `${count(f.failed)} ${f.failed === 1 ? 'run' : 'runs'} never arrived`;
  return { label, fact };
}

// ---------------------------------------------------------------- the layout

/** For the layout's own tests: every label candidate of a flow with its score, after placement. */
export type LabelTrace = (flowId: string, candidates: { x: number; y: number; anchor: 'start' | 'end'; cost: number; score: number }[]) => void;

export function layoutMap(nodesIn: MapNode[], flowsIn: MapFlow[], linksIn: MapLink[], options: LayoutOptions & { measure?: Measure; glyphScale?: number; trace?: LabelTrace }): MapLayoutX {
  const measure = options.measure ?? estimate;
  // Nodes with the same id: the first wins (a page may pass an absent ghost for a model it also found)
  const seen = new Set<string>();
  const nodes = nodesIn.filter((n) => !seen.has(n.id) && seen.add(n.id));
  const ids = new Set(nodes.map((n) => n.id));
  const flows = flowsIn.filter((f) => ids.has(f.from));
  const links = linksIn.filter((l) => ids.has(l.from) && ids.has(l.to) && l.from !== l.to);
  const offset = offsetsOf(nodes, flows, options.offsets);
  const laneOrder = laneOrderOf(nodes, flows, links, offset, options.laneOrder);
  // In time order; at the same time the busiest data model first, so a hub takes the first free column of its
  // lane, and one that feeds the map before one that only sends out of it (whose stub would push the feeder,
  // and everything after it, a column right); ties never fall to the order the data came in
  const degree = (id: string) => flows.filter((f) => f.from === id || f.to === id).length + links.filter((l) => l.from === id || l.to === id).length;
  const feeds = (id: string) => (flows.some((f) => f.from === id && f.to && f.to !== id && ids.has(f.to)) ? 1 : 0);
  const ordered = [...nodes].sort((a, b) =>
    offset.get(a.id)! - offset.get(b.id)! || degree(b.id) - degree(a.id) || feeds(b.id) - feeds(a.id) || (b.count ?? 0) - (a.count ?? 0) || a.id.localeCompare(b.id));
  const laneOf = (n: MapNode) => laneOrder.indexOf(n.system);
  const isOut = (f: MapFlow) => !f.to || f.to === f.from || !ids.has(f.to);

  // Columns in the order the records come: a box moves to the next column when its lane is taken or
  // something it comes from is in the current one; master data (no time) is the first column. A box
  // that sends out of the map keeps the slot right of it free for the ghost its arrow ends at. Boxes of
  // the same time (Now has no times, only the flows' order) do not push each other right: only a later time does.
  const col = new Map<string, number>();
  const taken = new Map<string, number>();
  let base = 0;
  let next = 0;
  let group = -1;
  let prevOffset = NaN;
  // A data model nothing flows to or from fills a hole once everything else sits
  const isolated = ordered.filter((n) => degree(n.id) === 0 && !options.offsets?.has(n.id));
  for (const n of ordered) {
    if (isolated.includes(n)) continue;
    const lane = laneOf(n);
    const off = offset.get(n.id)!;
    if (off !== prevOffset) {
      prevOffset = off;
      base = next;
      group++;
    }
    const master = off < MASTER / 10;
    const from = [
      ...flows.filter((f) => f.to === n.id && f.from !== n.id).map((f) => f.from),
      ...links.filter((l) => l.to === n.id).map((l) => l.from),
    ];
    let c = master ? 0 : base;
    const after = from.filter((x) => col.has(x)).map((x) => col.get(x)! + 1);
    if (after.length) c = Math.max(c, ...after);
    const wanted = c;
    let pushedByEarlier = false;
    while (taken.has(`${lane}|${c}`)) {
      if (taken.get(`${lane}|${c}`)! < group) pushedByEarlier = true;
      c++;
    }
    if (!master) next = Math.max(next, pushedByEarlier ? c : wanted);
    col.set(n.id, c);
    taken.set(`${lane}|${c}`, group);
    if (flows.some((f) => f.from === n.id && isOut(f))) taken.set(`${lane}|${c + 1}`, group);
  }
  for (const n of isolated) {
    const lane = laneOf(n);
    let c = 0;
    while (taken.has(`${lane}|${c}`)) c++;
    col.set(n.id, c);
    taken.set(`${lane}|${c}`, group + 1);
  }
  const cols = Math.max(1, ...nodes.map((n) => col.get(n.id)! + 1));

  // ---- what each column must hold: a node's title on one line at 15 px and its sub-line at 11 px (the
  // failure joins the sub-line when it fits, else gets its own line, so it does not widen the node), and
  // the ghosts of the out-flows in the slot right of their source
  const failTextOf = (n: MapNode) => (n.failed ? `✕ ${count(n.failed)} ${n.failedLabel ?? 'failed'}${n.failedWindow ? ` ${n.failedWindow}` : ''}` : '');
  const titleNeed = (n: MapNode) => measure(n.label, TITLE) + 2 * NODE_PAD_X;
  const subNeed = (n: MapNode) => Math.max(n.detail ? measure(n.detail, MONO) : 0, n.failed ? measure(failTextOf(n), MONO) : 0) + 2 * NODE_PAD_X;
  // Ghosts, one per source and destination (two processes to the same place share)
  type GhostDef = { key: string; from: string; slot: number; lane: number; label: string; fact: string | null; failed: boolean; flows: MapFlow[]; need: number };
  const ghostDefs = new Map<string, GhostDef>();
  for (const f of flows) {
    if (!isOut(f)) continue;
    const { label, fact } = ghostTextOf(f);
    const key = `${f.from}|${label.toLowerCase()}`;
    const g = ghostDefs.get(key);
    if (g) {
      g.flows.push(f);
      g.failed ||= f.failed > 0;
      // The streak's fact beats "never arrived", which beats nothing
      if (fact && (!g.fact || (f.failing?.since && !g.fact.startsWith('none since')))) g.fact = fact;
      continue;
    }
    const n = nodes.find((x) => x.id === f.from)!;
    ghostDefs.set(key, { key, from: f.from, slot: col.get(f.from)! + 1, lane: laneOf(n), label, fact, failed: f.failed > 0, flows: [f], need: 0 });
  }
  // A ghost's width: its name on one line and its fact; when the sub-lines wrap, its name wraps at its spaces too
  const ghostNeed = (g: GhostDef, wrap: boolean) => {
    const name = wrap ? Math.max(...g.label.split(' ').map((word) => measure(word, GHOST_F))) : measure(g.label, GHOST_F);
    return Math.max(GHOST_W_MIN, Math.min(GHOST_W_MAX, Math.max(name, g.fact ? measure(g.fact, MONO) : 0) + 2 * GHOST_PAD_X));
  };

  // ---- column widths: every column starts from one node width, as wide as the roomiest fit allows (up to
  // NODE_W), the columns that need more taking theirs first; the gap shares what is left down to GAP_TIGHT.
  // When even that is too wide, the sub-lines wrap at their separator ("118 records" / "7 min") so the
  // titles alone size the columns; and only when the map still does not fit is it wider than its box and
  // scrolls, with every node whole. A sparse map widens its nodes (up to NODE_W_MAX) before the gaps grow.
  const cap = Math.max(NODE_W_MIN, Math.min(NODE_W, options.nodeWidth ?? NODE_W));
  // The red line's width when it may break over two lines at a space: the wider half of the best split
  const failBroken = (n: MapNode) => {
    const words = failTextOf(n).split(' ');
    let best = measure(failTextOf(n), MONO);
    for (let i = 1; i < words.length; i++) best = Math.min(best, Math.max(measure(words.slice(0, i).join(' '), MONO), measure(words.slice(i).join(' '), MONO)));
    return best;
  };
  const fitColumns = (wrap: boolean, breakFail = false) => {
    const floor = wrap ? NODE_W_TIGHT : NODE_W_MIN;
    const need: number[] = Array.from({ length: cols }, () => floor);
    for (const n of nodes) {
      const c = col.get(n.id)!;
      // The red line never wraps: a column holds it even when the sub-lines wrap
      const failW = n.failed ? (breakFail ? failBroken(n) : measure(failTextOf(n), MONO)) + 2 * NODE_PAD_X : 0;
      const least = wrap ? Math.max(titleNeed(n), failW) : breakFail ? Math.max(titleNeed(n), failW, n.detail ? measure(n.detail, MONO) + 2 * NODE_PAD_X : 0) : Math.max(titleNeed(n), subNeed(n));
      need[c] = Math.max(need[c], Math.min(NODE_W_MAX, least));
    }
    let tail = 0;
    for (const g of ghostDefs.values()) {
      g.need = ghostNeed(g, wrap);
      if (g.slot < cols) need[g.slot] = Math.max(need[g.slot], g.need);
      else tail = Math.max(tail, g.need);
    }
    const avail = options.width - LABEL_W - PAD_R - tail;
    // A trailing ghost sits half a gap past the last column
    const slots = cols + (tail ? 0.5 : 0);
    const baseFor = (gapWanted: number, top: number) => {
      let nodeBase = top;
      for (let i = 0; i < 40; i++) {
        const extra = need.reduce((a, w) => a + Math.max(0, w - nodeBase), 0);
        const nextBase = Math.max(floor, Math.min(top, (avail - slots * gapWanted - extra) / cols));
        if (Math.abs(nextBase - nodeBase) < 0.05) return nextBase;
        nodeBase = nextBase;
      }
      return nodeBase;
    };
    let nodeBase = baseFor(GAP_MIN, cap);
    let colNodeW = need.map((w) => Math.max(nodeBase, w));
    let gap = (avail - colNodeW.reduce((a, w) => a + w, 0)) / slots;
    if (gap < GAP_MIN) {
      nodeBase = baseFor(GAP_TIGHT, cap);
      colNodeW = need.map((w) => Math.max(nodeBase, w));
      gap = (avail - colNodeW.reduce((a, w) => a + w, 0)) / slots;
    } else if (gap > GAP_WIDE && options.nodeWidth === undefined) {
      nodeBase = baseFor(GAP_WIDE, NODE_W_MAX);
      colNodeW = need.map((w) => Math.max(nodeBase, w));
      gap = (avail - colNodeW.reduce((a, w) => a + w, 0)) / slots;
    }
    gap = Math.max(GAP_TIGHT, gap);
    const used = colNodeW.reduce((a, w) => a + w, 0);
    return { wrap, breakFail, tail, slots, nodeBase, colNodeW, gap, overflow: used + slots * gap - avail };
  };
  // Tiers, each tried only when the one before overflows: as is; the red lines break over two lines (a node
  // grows by one line); the sub-lines wrap too; and at last the scroll
  let columns = fitColumns(false);
  if (columns.overflow > 0.5) {
    const tiers = [fitColumns(false, true), fitColumns(true), fitColumns(true, true)];
    const ok = tiers.find((t) => t.overflow <= 1);
    columns = ok ?? columns;
  }
  const { wrap: wrapDetails, breakFail, tail, nodeBase, colNodeW, gap } = columns;
  /** The gap centre left of column c (c = cols: right of the last column). */
  const colX: number[] = [LABEL_W];
  for (let c = 0; c < cols; c++) colX.push(colX[c] + gap + colNodeW[c]);
  const gapX = (g: number) => colX[Math.max(0, Math.min(cols, g))];
  const needW = colX[cols] + (tail ? gap / 2 + tail : 0) + PAD_R;
  const width = needW <= options.width + 1 ? options.width : needW;

  // Lanes adapt their height to what must fit; nodes shrink a little in tight lanes, and a label that
  // does not fit on one line at 15 px tries 13 px, then wraps, growing its node and its lane. Nothing is
  // ever cut: a detail line too wide for the node wraps too.
  const laneCount = laneOrder.length;
  let laneBase = options.maxHeight
    ? Math.max(LANE_MIN, Math.min(LANE_MAX, Math.floor(options.maxHeight / Math.max(1, laneCount))))
    : LANE_MAX;
  const sized = nodes.map((n) => {
    const w = colNodeW[col.get(n.id)!];
    const inner = w - 2 * NODE_PAD_X;
    let labelSize = 15;
    let labelLines = [n.label];
    if (measure(n.label, TITLE) > inner) {
      labelSize = 13;
      labelLines = wrapWords(n.label, inner, TITLE_S, measure);
    }
    const failText = failTextOf(n);
    const failOwnLine = !!failText && !!n.detail && measure(`${n.detail} · ${failText}`, MONO) > inner;
    // A detail a little wider than the text column runs into the padding; wider than that (or any wider,
    // when the columns were sized for titles alone) it breaks at its separator ("110 records" / "14 min")
    // rather than mid-phrase
    let detailLines: string[] = n.detail ? [n.detail] : [];
    if (n.detail && measure(n.detail, MONO) > (wrapDetails ? inner : inner + NODE_PAD_X - 2)) {
      const at = n.detail.lastIndexOf(' · ');
      detailLines = at > 0 ? [n.detail.slice(0, at), n.detail.slice(at + 3)] : [n.detail];
    }
    if (n.rows?.length) detailLines = [...detailLines, ...n.rows.flatMap((r) => wrapWords(r, inner, MONO, measure))];
    const failLines = failText ? (breakFail ? wrapWords(failText, inner, MONO, measure) : [failText]) : [];
    const lines = detailLines.length + (failText && (failOwnLine || !n.detail) ? failLines.length : 0);
    const textH = 2 * NODE_PAD_Y + labelLines.length * LINE_H[labelSize] + lines * DETAIL_H;
    return { n, w, labelSize, labelLines, textH, failText, failOwnLine, failLines, detailLines };
  });
  // The ghosts' size: their name wraps when the slot is narrower than it (never cut)
  const ghostSized = [...ghostDefs.values()].map((g) => {
    const w = Math.min(g.need, g.slot < cols ? colNodeW[g.slot] : tail);
    const labelLines = wrapWords(g.label, w - 2 * GHOST_PAD_X, GHOST_F, measure);
    const h = 2 * NODE_PAD_Y + labelLines.length * LINE_H[13] + (g.fact ? DETAIL_H : 0);
    return { g, w, labelLines, h };
  });
  // The ghosts of one node stack; their stack must fit the lane like a node
  const ghostStackH = new Map<string, number>();
  for (const s of ghostSized) ghostStackH.set(s.g.from, (ghostStackH.get(s.g.from) ?? -GHOST_STACK) + s.h + GHOST_STACK);
  // Node height follows the lane; a node with more text grows, and its lane with it (`pad` around it)
  const fit = (base: number, pad: number) => {
    const nodeH = Math.max(NODE_H_MIN, Math.min(NODE_H, base - 14));
    const heights = new Map(sized.map((s) => [s.n.id, Math.max(nodeH, s.textH)]));
    const tallestIn = (system: string) =>
      Math.max(nodeH, ...sized.filter((s) => s.n.system === system).map((s) => Math.max(heights.get(s.n.id)!, ghostStackH.get(s.n.id) ?? 0)));
    const lanes: Lane[] = [];
    let y = 0;
    laneOrder.forEach((system, index) => {
      const height = Math.max(base, tallestIn(system) + pad);
      lanes.push({ system, index, y, height });
      y += height;
    });
    const margin = Math.min(...lanes.map((l) => (l.height - tallestIn(l.system)) / 2));
    return { heights, lanes, total: y, margin };
  };
  let pad = 14;
  let fitted = fit(laneBase, pad);
  if (options.maxHeight) {
    // Lanes that grew take their room from the others, down to the minimum; past that the room around the
    // nodes gives way, and last the lanes go under the minimum: the map never grows past its height
    const shrink = (floor: number) => {
      while (fitted.total > options.maxHeight! && laneBase > floor) fitted = fit(--laneBase, pad);
    };
    shrink(LANE_MIN);
    for (const p of [10, 6, 4]) if (fitted.total > options.maxHeight) fitted = fit(laneBase, (pad = p));
    shrink(NODE_H_MIN + 2);
  }
  const { heights, lanes, margin } = fitted;
  const y = fitted.total;

  const placed: PlacedNodeX[] = sized.map(({ n, w, labelSize, labelLines, failText, failOwnLine, failLines, detailLines }) => {
    const lane = laneOf(n);
    const c = col.get(n.id)!;
    const h = heights.get(n.id)!;
    const x = colX[c] + gap / 2;
    const ny = lanes[lane].y + (lanes[lane].height - h) / 2;
    return { ...n, lane, col: c, x, y: ny, w, h, cx: x + w / 2, cy: ny + h / 2, labelLines, labelSize, failText, failOwnLine, failLines, detailLines };
  });
  const at = new Map(placed.map((p) => [p.id, p]));
  // Ghosts: in the slot right of their source, centred on it (a node's several stack around its middle)
  const ghosts: Ghost[] = [];
  const ghostOfFlow = new Map<string, Ghost>();
  const bySource = new Map<string, typeof ghostSized>();
  for (const s of ghostSized) bySource.set(s.g.from, [...(bySource.get(s.g.from) ?? []), s]);
  for (const [from, list] of bySource) {
    const A = at.get(from)!;
    list.sort((a, b) => a.g.label.localeCompare(b.g.label));
    const total = list.reduce((acc, s) => acc + s.h, 0) + GHOST_STACK * (list.length - 1);
    let gy = A.cy - total / 2;
    for (const { g, w, labelLines, h } of list) {
      const gx = colX[Math.min(g.slot, cols)] + gap / 2;
      const ghost: Ghost = { id: g.key, x: gx, y: gy, w, h, cx: gx + w / 2, cy: gy + h / 2, lane: g.lane, labelLines, fact: g.fact, failed: g.failed, flowIds: g.flows.map((f) => f.id) };
      ghosts.push(ghost);
      for (const f of g.flows) ghostOfFlow.set(f.id, ghost);
      gy += h + GHOST_STACK;
    }
  }

  // ---- routing
  const gapUse = new Map<number, number>();
  const gapStep = Math.max(4, Math.min(8, Math.floor((gap / 2 - 6) / 3)));
  const takeGap = (g: number) => {
    const k = gapUse.get(g) ?? 0;
    gapUse.set(g, k + 1);
    return cycled(k, gapStep, Math.max(0, gap / 2 - 8));
  };
  const corridorUse = new Map<number, number>();
  const backUse = new Map<string, number>();
  let busBelow = false;
  const corridorY = (b: number) => {
    if (b < lanes.length) return lanes[b].y;
    busBelow = true;
    return lanes[lanes.length - 1].y + lanes[lanes.length - 1].height + BUS_H / 2;
  };
  const takeCorridor = (b: number) => {
    const k = corridorUse.get(b) ?? 0;
    corridorUse.set(b, k + 1);
    // Lines along a lane's boundary keep NODE_CLEAR from the nodes above and below it: a busy corridor's lines draw
    // together on the boundary rather than run along a node's edge
    const room = b < lanes.length ? Math.max(0, margin - NODE_CLEAR) : BUS_H / 2 - 4;
    return cycled(k, Math.max(3, Math.min(6, Math.floor(room / 2))), Math.max(0, room));
  };

  // Arrows sharing a node's edge leave and arrive at distinct heights (top · middle · bottom), ordered by
  // where the other end is, so they do not cross
  const endDy = new Map<string, number>();
  const spread = (key: (f: MapFlow) => string | null, otherY: (f: MapFlow) => number) => {
    const groups = new Map<string, MapFlow[]>();
    for (const f of flows) {
      const k = key(f);
      if (k) groups.set(k, [...(groups.get(k) ?? []), f]);
    }
    for (const [k, fs] of groups) {
      const node = at.get(k.split('|')[1])!;
      fs.sort((a, b) => otherY(a) - otherY(b) || a.id.localeCompare(b.id));
      const step = fs.length > 1 ? Math.min(END_STEP, (node.h - 2 * END_PAD) / (fs.length - 1)) : 0;
      fs.forEach((f, i) => endDy.set(`${k}|${f.id}`, (i - (fs.length - 1) / 2) * step));
    }
  };
  const vertical = (f: MapFlow) => !isOut(f) && at.get(f.to!)!.col === at.get(f.from)!.col && Math.abs(at.get(f.to!)!.lane - at.get(f.from)!.lane) === 1;
  // Nodes many flows enter: their flows come in as one trunk (the same last gap and height) under one pill
  const into = new Map<string, MapFlow[]>();
  for (const f of flows) if (!isOut(f) && !vertical(f) && f.failed === 0) into.set(f.to!, [...(into.get(f.to!) ?? []), f]);
  const bundled = new Map([...into].filter(([, fs]) => fs.length >= BUNDLE_MIN));
  spread((f) => (vertical(f) ? null : `out|${f.from}`), (f) => (isOut(f) ? ghostOfFlow.get(f.id)?.cy ?? at.get(f.from)!.cy : at.get(f.to!)!.cy));
  spread((f) => (isOut(f) || vertical(f) || (bundled.has(f.to!) && f.failed === 0) ? null : `in|${f.to}`), (f) => at.get(f.from)!.cy);

  // Besides the points and kind: the column gap the documents run through (negative for a vertical hop: the column)
  const pointsFor = (A: PlacedNodeX, B: PlacedNodeX, dy0: number, dy1: number, trunk = false): { pts: Pt[]; kind: RoutedFlowX['kind']; gap: number } => {
    const y0 = A.cy + dy0;
    // A bundle's flows turn into the target's gap at its middle, so their last stretch is one line
    const lastGapX = (g: number) => (trunk && g === B.col ? gapX(g) : gapX(g) + takeGap(g));
    const y1 = B.cy + dy1;
    const S = { x: A.x + A.w, y: y0 };
    const E = { x: B.x, y: y1 };
    if (B.col === A.col && Math.abs(B.lane - A.lane) === 1) {
      const down = B.lane > A.lane;
      return { kind: 'vertical', gap: -(A.col + 1), pts: [{ x: A.cx, y: down ? A.y + A.h : A.y }, { x: B.cx, y: down ? B.y : B.y + B.h }] };
    }
    // One turn in a gap: the next column, or further right when the slots between are empty in one of
    // the two lanes (the run goes along the source's lane, or drops into the target's right away)
    const empty = (lane: number, from: number, to: number) => {
      for (let c = from + 1; c < to; c++) if (taken.has(`${lane}|${c}`)) return false;
      return true;
    };
    // A flow back to an earlier column does not loop round the map: it leaves the source's top or bottom
    // edge, runs along the lane boundary next to it and enters the target's bottom or top edge, when the
    // target's column is free in the lanes between
    if (B.col < A.col && B.lane !== A.lane) {
      const up = B.lane < A.lane;
      let free = true;
      for (let l = Math.min(A.lane, B.lane) + 1; l < Math.max(A.lane, B.lane); l++) if (taken.has(`${l}|${B.col}`)) free = false;
      if (free) {
        const b = up ? A.lane : A.lane + 1;
        const cy = corridorY(b) + takeCorridor(b);
        const k = backUse.get(B.id) ?? 0;
        backUse.set(B.id, k + 1);
        const bx = B.cx + cycled(k, 8, Math.max(0, B.w / 2 - 20));
        const ax = A.cx + cycled(k, 8, Math.max(0, A.w / 2 - 20));
        return {
          kind: 'corridor', gap: -(A.col + 1),
          pts: [{ x: ax, y: up ? A.y : A.y + A.h }, { x: ax, y: cy }, { x: bx, y: cy }, { x: bx, y: up ? B.y + B.h : B.y }],
        };
      }
    }
    if (B.col > A.col) {
      const g = empty(A.lane, A.col, B.col) ? B.col : empty(B.lane, A.col, B.col) ? A.col + 1 : null;
      if (g !== null) {
        if (Math.abs(y0 - y1) < 1) return { kind: 'adjacent', gap: g, pts: [S, E] };
        const xm = lastGapX(g);
        return { kind: 'adjacent', gap: g, pts: [S, { x: xm, y: y0 }, { x: xm, y: y1 }, E] };
      }
    }
    // Else along the boundary of the source's lane towards the target (the less used one in its own lane)
    const b = B.lane > A.lane ? A.lane + 1 : B.lane < A.lane ? A.lane
      : A.lane > 0 && (corridorUse.get(A.lane) ?? 0) < (corridorUse.get(A.lane + 1) ?? 0) ? A.lane : A.lane + 1;
    const cy = corridorY(b) + takeCorridor(b);
    const xa = gapX(A.col + 1) + takeGap(A.col + 1);
    const xb = lastGapX(B.col);
    return { kind: 'corridor', gap: A.col + 1, pts: [S, { x: xa, y: y0 }, { x: xa, y: cy }, { x: xb, y: cy }, { x: xb, y: y1 }, E] };
  };
  /** The stretch of a route (0..1) whose x lies in column gap g, sampled: where its documents stack with others'. */
  const windowIn = (route: Route, g: number): [number, number] => {
    if (g < 0) return [0, 1];
    const x0 = gapX(g) - gap / 2 - 1;
    const x1 = gapX(g) + gap / 2 + 1;
    let lo = 2;
    let hi = -1;
    for (let i = 0; i <= 64; i++) {
      const t = i / 64;
      const { x } = pointAlong(route, t);
      if (x >= x0 && x <= x1) {
        lo = Math.min(lo, t);
        hi = Math.max(hi, t);
      }
    }
    return lo <= hi ? [lo, hi] : [0.5, 0.5];
  };

  const order = [...flows].sort((a, b) => at.get(a.from)!.col - at.get(b.from)!.col || (at.get(a.to ?? '')?.col ?? 99) - (at.get(b.to ?? '')?.col ?? 99) || a.id.localeCompare(b.id));
  const routed: RoutedFlowX[] = order.map((f) => {
    const A = at.get(f.from)!;
    const dy0 = endDy.get(`out|${f.from}|${f.id}`) ?? 0;
    let pts: Pt[];
    let kind: RoutedFlowX['kind'];
    let gapAt: number;
    let B: PlacedNodeX | null = null;
    const ghost = ghostOfFlow.get(f.id) ?? null;
    if (isOut(f)) {
      // Across the gap into the ghost; with a turn in the gap when the ghost sits off the arrow's height
      kind = 'out';
      gapAt = A.col + 1;
      const y0 = A.cy + dy0;
      const G = ghost!;
      const y1 = Math.max(G.y + 6, Math.min(G.y + G.h - 6, y0));
      if (Math.abs(y1 - y0) < 1) pts = [{ x: A.x + A.w, y: y0 }, { x: G.x, y: y0 }];
      else {
        const xm = gapX(gapAt) + takeGap(gapAt);
        pts = [{ x: A.x + A.w, y: y0 }, { x: xm, y: y0 }, { x: xm, y: y1 }, { x: G.x, y: y1 }];
      }
    } else {
      B = at.get(f.to!)!;
      ({ pts, kind, gap: gapAt } = pointsFor(A, B, dy0, endDy.get(`in|${f.to}|${f.id}`) ?? 0, f.failed === 0 && bundled.has(f.to!)));
    }
    const route = routeOf(pts);
    const same = kind !== 'vertical' && (!B || Math.abs(A.cy - B.cy) < 1);
    const swapAt = kind === 'out' ? 0.5
      : same ? crossingAt(route, 'x', (pts[0].x + pts[pts.length - 1].x) / 2)
        : crossingAt(route, 'y', (pts[0].y + pts[pts.length - 1].y) / 2);
    // Fans: the last stretch (a split) or the first (a merge) shifted, one path per document
    const fans: Fan[] = [];
    const n = Math.min(MAX_FAN, f.split ?? f.merge ?? 0);
    if (n >= 2 && kind !== 'vertical' && B && pts[pts.length - 1].x !== B.x) {
      const half = Math.max(6, Math.min(A.h, B.h) / 2 - 8);
      const step = Math.min(6, (2 * half) / (n - 1));
      for (let k = 0; k < n; k++) {
        const off = (k - (n - 1) / 2) * step;
        let fp: Pt[];
        if (pts.length === 2) {
          const xm = gapX(B.col);
          fp = f.split
            ? [pts[0], { x: xm, y: pts[0].y }, { x: xm, y: pts[1].y + off }, { x: pts[1].x, y: pts[1].y + off }]
            : [{ x: pts[0].x, y: pts[0].y + off }, { x: xm, y: pts[0].y + off }, { x: xm, y: pts[1].y }, pts[1]];
        } else {
          fp = pts.map((p) => ({ ...p }));
          const shift = f.split ? [fp.length - 2, fp.length - 1] : [0, 1];
          for (const i of shift) fp[i].y += off;
        }
        const r = routeOf(fp);
        const sw = same ? crossingAt(r, 'x', (fp[0].x + fp[fp.length - 1].x) / 2) : crossingAt(r, 'y', (fp[0].y + fp[fp.length - 1].y) / 2);
        fans.push({ d: r.d, length: r.length, swapAt: sw, route: r });
      }
    }
    const labelText = f.timesLabel ?? f.label;
    return {
      ...f, d: route.d, length: route.length, swapAt, route, kind, fans, gapAt, gapWindow: windowIn(route, gapAt), ghost,
      labelText, labelAnchor: 'start', labelW: Math.ceil(measure(labelText, { size: LABEL_SIZE, weight: 500, mono: true })) + 12,
      labelAt: { x: 0, y: 0 },
    };
  });

  // One pill per bundle, on the flow of it whose source is nearest; it says how many flows and runs it carries
  for (const [to, fs] of bundled) {
    const members = routed.filter((r) => r.to === to && r.failed === 0 && fs.some((f) => f.id === r.id));
    if (members.length < BUNDLE_MIN) continue;
    const B = at.get(to)!;
    const carrier = [...members].sort((a, b) => Math.abs(at.get(a.from)!.cy - B.cy) - Math.abs(at.get(b.from)!.cy - B.cy) || a.id.localeCompare(b.id))[0];
    const runs = members.reduce((acc, m) => acc + m.runs, 0);
    for (const m of members) if (m !== carrier) m.labelHidden = true;
    carrier.bundle = { n: members.length, runs };
    carrier.labelText = `${members.length} integrations · ${count(runs)} ${runs === 1 ? 'run' : 'runs'}`;
    carrier.labelW = Math.ceil(measure(carrier.labelText, { size: LABEL_SIZE, weight: 500, mono: true })) + 12;
  }

  // ---- links by identifier: the same router, thin and dashed
  const routedLinks = links.map((l) => {
    const A = at.get(l.from)!;
    const B = at.get(l.to)!;
    const [a, b] = B.col >= A.col ? [A, B] : [B, A];
    return { ...l, d: routeOf(pointsFor(a, b, 0, 0).pts).d };
  });

  // ---- labels: one pill per edge, ON the edge's own line (its centre on the line), along the longest straight
  // runs, nudged along the run until it covers no node, ghost, other edge or other pill. A bundle of parallel
  // edges on one corridor shares the line, so its pills stack along the corridor at a fixed LABEL_STACK
  // spacing instead of on top of each other. Nothing ever floats off its edge, so there are no ticks.
  const height = y + (busBelow ? BUS_H : 0);
  const glyphBase = laneBase >= 80 ? 1 : laneBase >= 66 ? 0.9 : 0.8;
  const glyphS = 1.5 * glyphBase * (options.glyphScale ?? 1);
  type Obstacle = Rect & { kind: 'node' | 'path' | 'label'; owner?: string; vertical?: boolean; at?: number };
  const obstacles: Obstacle[] = [
    ...placed.map((p) => ({ x: p.x, y: p.y, w: p.w, h: p.h, kind: 'node' as const })),
    ...ghosts.map((g) => ({ x: g.x, y: g.y, w: g.w, h: g.h, kind: 'node' as const })),
  ];
  const THIN = 2;
  const addRoute = (r: Route, owner: string) => {
    for (const p of r.prims) {
      if (p.kind === 'L') {
        const vertical = Math.abs(p.x1 - p.x0) < 0.5;
        obstacles.push({
          x: Math.min(p.x0, p.x1) - THIN, y: Math.min(p.y0, p.y1) - THIN,
          w: Math.abs(p.x1 - p.x0) + 2 * THIN, h: Math.abs(p.y1 - p.y0) + 2 * THIN, kind: 'path', owner,
          vertical, at: vertical ? p.x0 : p.y0,
        });
      } else obstacles.push({ x: p.cx - p.r - THIN, y: p.cy - p.r - THIN, w: 2 * (p.r + THIN), h: 2 * (p.r + THIN), kind: 'path', owner });
    }
  };
  for (const f of routed) {
    addRoute(f.route, f.id);
    for (const fan of f.fans) addRoute(fan.route, f.id);
    if (f.failed > 0) {
      const E = f.route.pts[f.route.pts.length - 1];
      obstacles.push({ x: E.x - 14, y: E.y - 12, w: 28, h: 40, kind: 'path', owner: f.id });
    }
  }
  // A node is worse than anything (far worse when covered), another pill nearly as bad, another edge less so
  const PENALTY = { node: [600, 1400], path: [90, 200], label: [300, 600] } as const;
  const box = (x: number, y: number, w: number, anchor: 'start' | 'end'): Rect => ({ x: anchor === 'end' ? x - w : x, y: y - LABEL_H / 2, w, h: LABEL_H });
  const inside = (b: Rect) => b.x >= LABEL_W + 2 && b.x + b.w <= width - 2 && b.y >= 1 && b.y + b.h <= height - 1;
  const overlap = (a: Rect, b: Rect) => {
    const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
    const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
    return (w * h) / (a.w * a.h);
  };
  type Run = { vertical: boolean; at: number };
  const penalty = (b: Rect, skip: Obstacle | null, owner: string, run: Run | null) =>
    obstacles.reduce((acc, o) => {
      // A pill keeps a few pixels of air from another pill
      if (o === skip || !hits(o.kind === 'label' ? { x: b.x - LABEL_GAP, y: b.y - 1, w: b.w + 2 * LABEL_GAP, h: b.h + 2 } : b, o)) return acc;
      if (o.kind === 'path') {
        if (o.owner === owner) return acc;
        // Another edge on the same corridor, a few pixels off our line: the bundle shares it
        if (run && o.vertical === run.vertical && o.at !== undefined && Math.abs(o.at - run.at) <= 8) return acc;
      }
      return acc + PENALTY[o.kind][0] + PENALTY[o.kind][1] * overlap(b, o);
    }, 0);
  /** Where the pill's tick meets its line, when the pill sits beside the line instead of on it. */
  type Cand = { x: number; y: number; cost: number; run: Run; tie?: { x: number; y: number; vertical: boolean } };
  const LABEL_GAP = 3;
  // Positions along a run, nearest the favoured 0.55 first, every 4 % of the run
  const FRACS = Array.from({ length: 25 }, (_, i) => 0.02 + i * 0.04).sort((a, b) => Math.abs(a - 0.55) - Math.abs(b - 0.55));
  const candidatesOf = (f: RoutedFlowX): Cand[] => {
    const w = f.labelW;
    const out: Cand[] = [];
    const runs = f.route.prims.filter((p): p is Extract<Primitive, { kind: 'L' }> => p.kind === 'L' && p.len >= 12);
    const byLen = [...runs].sort((a, b) => b.len - a.len);
    for (const p of runs) {
      const base = byLen.indexOf(p) * 1.5;
      const vertical = Math.abs(p.x1 - p.x0) < 0.5;
      const lo = vertical ? Math.min(p.y0, p.y1) : Math.min(p.x0, p.x1);
      const hi = vertical ? Math.max(p.y0, p.y1) : Math.max(p.x0, p.x1);
      const ext = vertical ? LABEL_H : w;
      const over = Math.max(0, ext + 8 - (hi - lo)) / 8;
      // Stacked rows: a bundle's pills sit LABEL_STACK apart along the corridor
      for (const k of [0, 1, -1, 2, -2, 3, -3]) {
        for (let i = 0; i < FRACS.length; i++) {
          const centre = Math.max(lo, Math.min(hi, lo + (hi - lo) * FRACS[i] + (vertical ? k * LABEL_STACK : k * (w + 4))));
          const cost = base + i * 0.25 + Math.abs(k) * 0.6 + over;
          // When nothing on the line is free, a pill hugs it from the side (a last resort)
          for (const [side, extra] of [[0, 0], [-1, 12], [1, 12]] as const) {
            const o = side * (LABEL_H + 1);
            if (vertical) out.push({ x: p.x0 - w / 2 + (side ? side * (w / 2 + 4) : 0), y: centre, cost: cost + extra, run: { vertical, at: p.x0 }, tie: side ? { x: p.x0, y: centre, vertical } : undefined });
            else out.push({ x: centre - w / 2, y: p.y0 + o, cost: cost + extra, run: { vertical, at: p.y0 }, tie: side ? { x: centre, y: p.y0, vertical } : undefined });
          }
        }
      }
    }
    // Last resort, when the arrow is too short or crowded: over and under the node (or ghost) it ends at, then its source
    const ends: Rect[] = [at.get(f.from)!, ...(f.kind === 'out' ? [f.ghost!] : f.to ? [at.get(f.to)!] : [])];
    ends.forEach((n, e) => {
      for (let row = 0; row < 3; row++) {
        for (const [yy, d] of [[n.y - LABEL_H / 2 - 2 - row * LABEL_STACK, 0], [n.y + n.h + LABEL_H / 2 + 2 + row * LABEL_STACK, 0.5]] as const) {
          out.push({ x: n.x + 8, y: yy, cost: 30 + e * 3 + row + d, run: { vertical: false, at: -1e9 } });
          out.push({ x: n.x + n.w - 8 - w, y: yy, cost: 31 + e * 3 + row + d, run: { vertical: false, at: -1e9 } });
        }
      }
    });
    return out;
  };
  const place = (f: RoutedFlowX, own: Obstacle | null) => {
    const seenC = new Map<string, Cand>();
    for (const c of candidatesOf(f)) {
      const key = `${Math.round(c.x)}|${Math.round(c.y)}`;
      const prev = seenC.get(key);
      if (!prev || prev.cost > c.cost) seenC.set(key, c);
    }
    let best: { c: Cand; score: number } | null = null;
    const scored: { x: number; y: number; anchor: 'start' | 'end'; cost: number; score: number }[] = [];
    for (const c of seenC.values()) {
      const b = box(c.x, c.y, f.labelW, 'start');
      const score = c.cost + penalty(b, own, f.id, c.run) + (inside(b) ? 0 : 5000);
      if (options.trace) scored.push({ x: c.x, y: c.y, anchor: 'start', cost: c.cost, score });
      if (!best || score < best.score) best = { c, score };
    }
    options.trace?.(f.id, scored);
    const pick = best?.c ?? { x: f.route.pts[0].x, y: f.route.pts[0].y, cost: 0, run: { vertical: false, at: 0 } };
    f.labelAt = { x: pick.x, y: pick.y };
    f.labelAnchor = 'start';
    // Displaced from its line: a tick joins the pill to its arrow
    const t = pick.tie;
    f.leader = t
      ? (t.vertical
        ? { x1: t.x, y1: t.y, x2: pick.x + f.labelW / 2 < t.x ? pick.x + f.labelW : pick.x, y2: t.y }
        : { x1: t.x, y1: t.y, x2: t.x, y2: pick.y < t.y ? pick.y + LABEL_H / 2 : pick.y - LABEL_H / 2 })
      : null;
    return { ...box(pick.x, pick.y, f.labelW, 'start'), kind: 'label' as const };
  };
  const labelBoxes = new Map<string, Obstacle>();
  // The flows with the least room first: failing ones (their labels matter most), then the long labels
  const byNeed = [...routed].filter((f) => !f.labelHidden).sort((a, b) => Number(b.failed > 0) - Number(a.failed > 0) || b.labelW - a.labelW);
  for (const f of byNeed) {
    const b = place(f, null);
    labelBoxes.set(f.id, b);
    obstacles.push(b);
  }
  for (const f of byNeed) {
    const own = labelBoxes.get(f.id)!;
    const b = place(f, own);
    Object.assign(own, b);
  }

  return { lanes, nodes: placed, flows: routed, ghosts, links: routedLinks, width, height, colW: nodeBase + gap, nodeW: nodeBase, gap, glyphBase, glyphS };
}
